import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Settings, Sun, Moon, Compass } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { useTour } from "../../context/TourContext";
import { notificationsAPI } from "../../api";
import ChickenLogo from "../ChickenLogo";
import "./Navbar.css";

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { startTour } = useTour();
  const navigate = useNavigate();
  const [notificationCounts, setNotificationCounts] = useState({
    unread_messages: 0,
    unread_notifications: 0,
    pending_orders: 0,
    new_orders: 0
  });
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const settingsRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) return;

    const loadNotificationCounts = async () => {
      try {
        const response = await notificationsAPI.getCounts();
        setNotificationCounts(response.data);
        // Log pour déboguer les commandes
        const totalOrders = response.data.pending_orders + response.data.new_orders;
        if (totalOrders > 0) {
          console.log("Orders badge:", {
            pending_orders: response.data.pending_orders,
            new_orders: response.data.new_orders,
            total: totalOrders
          });
        }
      } catch (err) {
        console.error("Error loading notification counts:", err);
      }
    };

    loadNotificationCounts();
    
    const interval = setInterval(loadNotificationCounts, 10000); // Rafraîchir toutes les 10 secondes

    return () => {
      clearInterval(interval);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setIsSettingsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const closeSettings = () => setIsSettingsOpen(false);

  const handleLogout = () => {
    closeSettings();
    logout();
    navigate("/");
  };

  const handleStartTour = () => {
    closeSettings();
    startTour();
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-brand">
          <ChickenLogo size={26} className="navbar-logo-icon" />
          PouletDirect
        </Link>
        
        <div className="navbar-menu">
          {isAuthenticated ? (
            <div className="navbar-tabs-authenticated">
              <NavLink to="/products" className={({ isActive }) => `navbar-link${isActive ? " active" : ""}`}>
                Produits
              </NavLink>

              <NavLink to="/orders" className={({ isActive }) => `navbar-link navbar-link-with-badge${isActive ? " active" : ""}`}>
                Commandes
                {notificationCounts.pending_orders > 0 && (
                  <span className="navbar-badge">
                    {notificationCounts.pending_orders}
                  </span>
                )}
              </NavLink>

              <NavLink to="/messages" className={({ isActive }) => `navbar-link navbar-link-with-badge${isActive ? " active" : ""}`}>
                Messages
                {notificationCounts.unread_messages > 0 && (
                  <span className="navbar-badge">{notificationCounts.unread_messages}</span>
                )}
              </NavLink>

              <NavLink to="/profile" className={({ isActive }) => `navbar-link${isActive ? " active" : ""}`}>
                Profil
              </NavLink>
            </div>
          ) : (
            <>
              <NavLink to="/products" className={({ isActive }) => `navbar-link${isActive ? " active" : ""}`}>
                Produits
              </NavLink>
              <Link to="/login" className="btn btn-outline btn-sm">
                Connexion
              </Link>
              <Link to="/register" className="btn btn-primary btn-sm">
                Inscription
              </Link>
            </>
          )}
        </div>

        <div className="navbar-actions">
          {isAuthenticated && (
            <div className="navbar-settings" ref={settingsRef}>
              <button
                type="button"
                onClick={() => setIsSettingsOpen((open) => !open)}
                className="navbar-theme-toggle"
                aria-label="Paramètres"
                aria-expanded={isSettingsOpen}
                title="Paramètres"
                data-tour="navbar-settings-toggle"
              >
                <Settings size={17} strokeWidth={2.25} />
                {notificationCounts.unread_notifications > 0 && (
                  <span className="navbar-settings-dot" />
                )}
              </button>

              {isSettingsOpen && (
                <div className="navbar-settings-menu">
                  <div className="navbar-settings-user">{user.name}</div>

                  <div className="navbar-settings-mobile-tabs">
                    <NavLink to="/products" className="navbar-settings-link" onClick={closeSettings}>
                      Produits
                    </NavLink>
                    <NavLink to="/orders" className="navbar-settings-link" onClick={closeSettings}>
                      Commandes
                      {notificationCounts.pending_orders > 0 && (
                        <span className="navbar-badge">{notificationCounts.pending_orders}</span>
                      )}
                    </NavLink>
                    <NavLink to="/messages" className="navbar-settings-link" onClick={closeSettings}>
                      Messages
                      {notificationCounts.unread_messages > 0 && (
                        <span className="navbar-badge">{notificationCounts.unread_messages}</span>
                      )}
                    </NavLink>
                    <NavLink to="/profile" className="navbar-settings-link" onClick={closeSettings}>
                      Profil
                    </NavLink>
                  </div>

                  {user.role === "breeder" && (
                    <>
                      <NavLink to="/my-products" className="navbar-settings-link" onClick={closeSettings}>
                        Mes produits
                      </NavLink>
                      <NavLink to="/products/create" className="navbar-settings-link" onClick={closeSettings}>
                        Ajouter un produit
                      </NavLink>
                    </>
                  )}

                  <NavLink to="/stats" className="navbar-settings-link" onClick={closeSettings}>
                    Statistiques
                  </NavLink>

                  <NavLink to="/notifications" className="navbar-settings-link" onClick={closeSettings}>
                    Notifications
                    {notificationCounts.unread_notifications > 0 && (
                      <span className="navbar-badge navbar-badge-notifications">
                        {notificationCounts.unread_notifications}
                      </span>
                    )}
                  </NavLink>

                  <button onClick={handleStartTour} className="navbar-settings-link navbar-tour-trigger">
                    <Compass size={15} strokeWidth={2.25} /> Visite guidée
                  </button>

                  <button onClick={handleLogout} className="navbar-settings-link navbar-settings-logout">
                    Déconnexion
                  </button>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={toggleTheme}
            className="navbar-theme-toggle"
            aria-label={theme === "dark" ? "Passer en mode normal" : "Passer en mode sombre"}
            title={theme === "dark" ? "Mode normal" : "Mode sombre"}
          >
            {theme === "dark" ? <Sun size={17} strokeWidth={2.25} /> : <Moon size={17} strokeWidth={2.25} />}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
